import { useEffect, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { spaceErrorMessage, spacesApi, type SpaceGrepResult, type SpaceNodeView, type SpaceSearchResult } from '../../lib/api';
import { INDEX_STATUS_LABEL, formatBytes } from '../../lib/spaces';
import { cn, timeAgo } from '../../lib/utils';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Snippet } from '../ui/snippet';
import { NodeIcon } from './FolderView';

/** What is being asked, all of it held in the URL by the page. */
export interface SpaceSearchParams {
  q: string;
  /** Exact text (grep) rather than words (search). */
  exact: boolean;
  /** The folder the search began in, offered as a narrower scope. */
  from: string | null;
  /** Searching beneath `from` only. */
  within: boolean;
}

type Results =
  | { kind: 'words'; result: SpaceSearchResult }
  | { kind: 'exact'; result: SpaceGrepResult };

/** The folder an address sits in: `~/research/q3/notes.md` → `~/research/q3/`. */
function folderOf(address: string): string {
  return address.slice(0, address.lastIndexOf('/') + 1);
}

/** A line with the first occurrence of `pattern` marked, ignoring case as the search did. */
function MarkedLine({ line, pattern }: { line: string; pattern: string }) {
  const at = line.toLowerCase().indexOf(pattern.toLowerCase());
  if (at < 0 || pattern.length === 0) return <>{line}</>;
  return (
    <>
      {line.slice(0, at)}
      <mark className="rounded-sm bg-orange-100 px-0.5 text-ink">{line.slice(at, at + pattern.length)}</mark>
      {line.slice(at + pattern.length)}
    </>
  );
}

function Chip({ children, title }: { children: React.ReactNode; title: string }) {
  return (
    <span className="flex-shrink-0 rounded-full bg-orange-100 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-orange-700" title={title}>
      {children}
    </span>
  );
}

function HitRow({ file, onOpen, children }: { file: SpaceNodeView; onOpen: () => void; children?: React.ReactNode }) {
  const status = file.indexStatus && file.indexStatus !== 'NONE' && file.indexStatus !== 'INDEXED' ? INDEX_STATUS_LABEL[file.indexStatus] : null;
  return (
    <li>
      <button type="button" onClick={onOpen} className="w-full text-left px-4 py-3 hover:bg-surface-subtle transition-colors">
        <div className="flex items-center gap-2 min-w-0">
          <NodeIcon node={file} />
          <span className="text-xs font-medium text-ink truncate">{file.name}</span>
          {status && <span className="text-[10px] text-ink-faint flex-shrink-0">{status}</span>}
          <span className="ml-auto flex-shrink-0 text-[10px] text-ink-faint whitespace-nowrap tabular-nums">
            {formatBytes(file.sizeBytes)} · {timeAgo(file.updatedAt)}
          </span>
        </div>
        <p className="mt-0.5 pl-[22px] text-[10px] font-mono text-ink-faint truncate">{folderOf(file.address)}</p>
        {children}
      </button>
    </li>
  );
}

/**
 * Searching the files you can see: your own space and everything shared with you, or only the folder
 * the search began in.
 *
 * Two modes over the same scope. **Words** (`GET /spaces/search`) matches any word and ranks files
 * holding every word first — the knowledge base's lexical search — and also matches file names, so an
 * image or a file too large to read is still found by what it is called. **Exact text**
 * (`GET /spaces/grep`) finds the text as typed, ignoring case, and shows the line it is on. Both read
 * only what has been indexed: a file whose text is still being read is not in them yet.
 *
 * A result opens the file panel over the results, so closing it comes back here.
 */
export function SearchView({
  params, onChange, onOpenFile,
}: {
  params: SpaceSearchParams;
  onChange: (next: SpaceSearchParams) => void;
  onOpenFile: (file: SpaceNodeView) => void;
}) {
  const [draft, setDraft] = useState(params.q);
  const [results, setResults] = useState<Results | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [fromAddress, setFromAddress] = useState<string | null>(null);
  const request = useRef(0);

  useEffect(() => setDraft(params.q), [params.q]);

  // The folder offered as a scope, named the way the caller sees it.
  useEffect(() => {
    setFromAddress(null);
    if (!params.from) return;
    let live = true;
    spacesApi
      .folder(params.from, { limit: 1 })
      .then((listing) => live && setFromAddress(listing.breadcrumbs[listing.breadcrumbs.length - 1]?.address ?? null))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [params.from]);

  const folder = params.within ? params.from : null;
  useEffect(() => {
    const q = params.q.trim();
    if (!q) {
      setResults(null);
      setError(null);
      return;
    }
    // Only the latest answer is shown: typing quickly must not let a slow earlier search land last.
    const id = ++request.current;
    setLoading(true);
    setError(null);
    const run: Promise<Results> = params.exact
      ? spacesApi.grep({ pattern: q, folder }).then((result) => ({ kind: 'exact' as const, result }))
      : spacesApi.search({ q, folder }).then((result) => ({ kind: 'words' as const, result }));
    run
      .then((next) => id === request.current && setResults(next))
      .catch((err) => id === request.current && setError(spaceErrorMessage(err, 'Could not search')))
      .finally(() => id === request.current && setLoading(false));
  }, [params.q, params.exact, folder]);

  const count = results ? results.result.hits.length : 0;

  return (
    <div className="max-w-3xl">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onChange({ ...params, q: draft.trim() });
        }}
        className="relative"
      >
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint pointer-events-none" />
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={params.exact ? 'Exact text, as it appears in the file' : 'Words in a file, or its name'}
          className="pl-8"
          autoFocus
          aria-label="Search your files"
        />
      </form>

      <div className="mt-3 flex items-center gap-4 flex-wrap text-xs text-ink-muted">
        {params.from && (
          <div className="flex items-center gap-1 rounded-md border border-border p-0.5">
            <button
              type="button"
              onClick={() => onChange({ ...params, within: false })}
              className={cn('px-2 py-0.5 rounded', !params.within ? 'bg-surface-muted text-ink font-medium' : 'hover:text-ink')}
            >
              Everywhere
            </button>
            <button
              type="button"
              onClick={() => onChange({ ...params, within: true })}
              className={cn('px-2 py-0.5 rounded max-w-[18rem] truncate', params.within ? 'bg-surface-muted text-ink font-medium' : 'hover:text-ink')}
              title={fromAddress ?? undefined}
            >
              In <span className="font-mono">{fromAddress ?? 'this folder'}</span>
            </button>
          </div>
        )}
        <label className="flex items-center gap-1.5 cursor-pointer">
          <Checkbox checked={params.exact} onChange={(e) => onChange({ ...params, exact: e.target.checked })} />
          Exact text
        </label>
        {results && !loading && (
          <span className="ml-auto text-[11px] text-ink-faint">
            {count === 0 ? 'No files' : count === 1 ? '1 file' : `${count} files`}
          </span>
        )}
      </div>

      <div className="mt-4">
        {error ? (
          <div className="rounded-md border border-danger-ring bg-danger-bg px-4 py-3 text-sm text-danger-text">{error}</div>
        ) : !params.q.trim() ? (
          <p className="text-xs text-ink-faint">
            Search the text of your files and of everything shared with you, by its words and by what it is about. PDFs, Word, PowerPoint and Excel files are searchable once their text has been read, usually within a minute of uploading.
          </p>
        ) : loading && !results ? (
          <p className="text-xs text-ink-faint">Searching…</p>
        ) : results && count === 0 ? (
          <p className="text-xs text-ink-faint">
            Nothing you can see matches. A file uploaded in the last minute may still be being read.
          </p>
        ) : results ? (
          <ul className={cn('rounded-md border border-border divide-y divide-border-subtle', loading && 'opacity-60')}>
            {results.kind === 'words'
              ? results.result.hits.map((hit) => (
                  <HitRow key={hit.file.id} file={hit.file} onOpen={() => onOpenFile(hit.file)}>
                    {(hit.allTerms || (hit.nameMatch && !hit.snippet) || (hit.semantic && !hit.textMatch)) && (
                      <div className="mt-1 pl-[22px] flex items-center gap-1.5">
                        {hit.allTerms && <Chip title="Holds every word you searched for, not just one">every term</Chip>}
                        {hit.nameMatch && !hit.snippet && <Chip title="Matched on the file's name">name</Chip>}
                        {hit.semantic && !hit.textMatch && (
                          <Chip title="None of your words, but a passage about the same thing — the start of it is shown">related</Chip>
                        )}
                      </div>
                    )}
                    {hit.snippet && (
                      <p className="mt-1 pl-[22px] line-clamp-3 text-[11px] leading-relaxed text-ink-faint">
                        {hit.heading && (
                          <span className="text-ink-muted font-medium">
                            <Snippet text={hit.heading} /> ·{' '}
                          </span>
                        )}
                        <Snippet text={hit.snippet} />
                      </p>
                    )}
                  </HitRow>
                ))
              : results.result.hits.map((hit) => (
                  <HitRow key={hit.file.id} file={hit.file} onOpen={() => onOpenFile(hit.file)}>
                    <p className="mt-1 pl-[22px] text-[11px] font-mono text-ink-secondary truncate">
                      {hit.heading && <span className="text-ink-muted font-sans font-medium">{hit.heading} · </span>}
                      {hit.line ? <MarkedLine line={hit.line} pattern={results.result.pattern} /> : '…'}
                    </p>
                  </HitRow>
                ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
}
